import React, { useEffect, useId, useState } from "react";
import { useDispatch } from "react-redux";

import { XboxSpinner } from "@gamedev-web/gamedev-sdk-core";
import { Icon, IconSize } from "@gaming/xds-react-js";
import { Clock, Close, Warning } from "@gaming/xds-react-js-icons";

import { xboxOnboardSiteLocationPrefix } from "../../constants";
import {
  clearNdaResendCooldown,
  getNdaResendAvailableAt,
  startInitialNdaResendCooldown,
  startNdaResendCooldown,
} from "../../helpers/ndaResendCooldown";
import { useStrings } from "../../localization/localizationHelpers";
import { xboxOnboardActions } from "../../redux/actions/xboxOnboardActions";
import type { LegalEntityNameChangeState } from "../../server/crm/api/models/xboxOnboardModels";
import { Modal, ModalInitialFocus } from "../Modal/Modal";
import * as styles from "./LegalEntityNameChangeStatus.module.css";

type LegalEntityNameChangeStatusProps = Readonly<{
  state: LegalEntityNameChangeState;
  contactEmail?: string;
  attached?: boolean;
  flush?: boolean;
  onStatusChanged?: () => void;
}>;

export const LegalEntityNameChangeStatus = ({
  state,
  contactEmail,
  attached = false,
  flush = false,
  onStatusChanged,
}: LegalEntityNameChangeStatusProps) => {
  const dispatch = useDispatch();
  const titleId = useId();
  const cancelTitleId = useId();
  const [isLearnMoreOpen, setIsLearnMoreOpen] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [isResendCooldownLoaded, setIsResendCooldownLoaded] = useState(false);
  const [ndaResendAvailableAt, setNdaResendAvailableAt] = useState<number | null>(null);
  const [hasResendError, setHasResendError] = useState(false);
  const [hasCancelError, setHasCancelError] = useState(false);
  const [
    ndaSentTitle,
    ndaSentDescription,
    approvedAwaitingNdaTitle,
    requestedLegalEntityNameLabel,
    requestedByLabel,
    resendLabel,
    resendError,
    reviewTitle,
    reviewDescription,
    learnMoreLabel,
    reviewDuration,
    reviewModalTitle,
    reviewModalDescription,
    reviewModalUnavailableTitle,
    reviewModalDevKits,
    reviewModalSigning,
    reviewModalAccount,
    reviewModalUpdate,
    reviewModalDuration,
    closeLabel,
    cancelButtonLabel,
    cancelRequestLabel,
    cancelRequestTitle,
    cancelRequestDescription,
    cancelRequestError,
  ] = useStrings(
    "legalEntityNameChangeNdaSentTitle",
    "legalEntityNameChangeNdaSentDescription",
    "legalEntityNameChangeApprovedAwaitingNdaTitle",
    "legalEntityNameChangeRequestedName",
    "legalEntityNameChangeRequestedBy",
    "partnerLegalResendButton",
    "legalEntityNameChangeNdaResendError",
    "legalEntityNameChangeReviewTitle",
    "legalEntityNameChangeReviewDescription",
    "learnMore",
    "legalEntityNameChangeReviewDuration",
    "legalEntityNameChangeReviewModalTitle",
    "legalEntityNameChangeReviewModalDescription",
    "legalEntityNameChangeReviewModalUnavailableTitle",
    "legalEntityNameChangeReviewModalDevKits",
    "legalEntityNameChangeReviewModalSigning",
    "legalEntityNameChangeReviewModalAccount",
    "legalEntityNameChangeReviewModalUpdate",
    "legalEntityNameChangeReviewModalDuration",
    "closeAriaLabel",
    "cancelButton",
    "legalEntityNameChangeCancelLabel",
    "legalEntityNameChangeCancelTitle",
    "legalEntityNameChangeCancelDescription",
    "legalEntityNameChangeCancelError"
  );
  const currentLegalEntityName = state.currentLegalEntityName?.trim() ?? "";
  const requestedLegalEntityName = state.requestedLegalEntityName?.trim();
  const requesterDisplayName = state.requesterDisplayName?.trim();
  const isNdaResendCooldownActive = ndaResendAvailableAt !== null;
  const requesterDetails = requesterDisplayName ? (
    <p>{requestedByLabel.replace("{0}", requesterDisplayName)}</p>
  ) : null;

  const handleResend = async () => {
    setIsResending(true);
    setHasResendError(false);
    try {
      const response = await fetch(
        `${xboxOnboardSiteLocationPrefix}/settings/api/legal-entity-name-change/resend-nda`,
        { method: "POST" }
      );
      if (!response.ok) {
        throw new Error(`Legal entity name change NDA resend failed: ${response.status}`);
      }
      if (contactEmail) {
        setNdaResendAvailableAt(startNdaResendCooldown(window.localStorage, contactEmail));
      }
      dispatch(xboxOnboardActions.fetchData.start());
      dispatch(xboxOnboardActions.pollNda.start());
      onStatusChanged?.();
    } catch {
      setHasResendError(true);
    } finally {
      setIsResending(false);
    }
  };

  const handleCancel = async () => {
    setIsCanceling(true);
    setHasCancelError(false);
    try {
      const response = await fetch(
        `${xboxOnboardSiteLocationPrefix}/settings/api/legal-entity-name-change/cancel`,
        { method: "POST" }
      );
      if (!response.ok) {
        throw new Error(`Legal entity name change cancellation failed: ${response.status}`);
      }
      setIsCancelConfirmOpen(false);
      dispatch(xboxOnboardActions.fetchData.start());
      onStatusChanged?.();
    } catch {
      setHasCancelError(true);
    } finally {
      setIsCanceling(false);
    }
  };

  useEffect(() => {
    if (!contactEmail) {
      setIsResendCooldownLoaded(true);
      return;
    }

    const availableAt = state.hasNdaSent
      ? startInitialNdaResendCooldown(window.localStorage, contactEmail)
      : getNdaResendAvailableAt(window.localStorage, contactEmail);
    setNdaResendAvailableAt(availableAt);
    setIsResendCooldownLoaded(true);
  }, [contactEmail, state.hasNdaSent]);

  useEffect(() => {
    if (!contactEmail || ndaResendAvailableAt === null) {
      return;
    }

    const remainingMs = ndaResendAvailableAt - Date.now();
    if (remainingMs <= 0) {
      clearNdaResendCooldown(window.localStorage, contactEmail);
      setNdaResendAvailableAt(null);
      return;
    }

    const timeout = window.setTimeout(() => {
      clearNdaResendCooldown(window.localStorage, contactEmail);
      setNdaResendAvailableAt(null);
    }, remainingMs);

    return () => window.clearTimeout(timeout);
  }, [contactEmail, ndaResendAvailableAt]);

  const cancelButton = state.canCancelInquiry ? (
    <button
      type="button"
      className={`${styles.resendButton} ${styles.cancelRequestButton}`}
      disabled={isCanceling || isResending}
      onClick={() => {
        setHasCancelError(false);
        setIsCancelConfirmOpen(true);
      }}
    >
      {cancelRequestLabel}
    </button>
  ) : null;
  const cancelModal = (
    <Modal
      isOpen={isCancelConfirmOpen}
      onClose={() => setIsCancelConfirmOpen(false)}
      ariaLabelledBy={cancelTitleId}
      initialFocus={ModalInitialFocus.FirstButton}
      closeOnOutsideClick={!isCanceling}
      width={448}
    >
      <div className={styles.modal} aria-busy={isCanceling}>
        <div className={styles.modalHeader}>
          <h2 id={cancelTitleId}>{cancelRequestTitle}</h2>
          <button
            type="button"
            aria-label={closeLabel}
            disabled={isCanceling}
            onClick={() => setIsCancelConfirmOpen(false)}
          >
            <Icon aria-hidden="true" icon={Close} iconSize={IconSize.Custom} />
          </button>
        </div>
        <p>{cancelRequestDescription}</p>
        {hasCancelError && <p role="alert">{cancelRequestError}</p>}
        <div className={styles.modalActions}>
          <button
            type="button"
            disabled={isCanceling}
            onClick={() => setIsCancelConfirmOpen(false)}
          >
            {cancelButtonLabel}
          </button>
          <button
            type="button"
            className={styles.cancelRequestButton}
            aria-label={cancelRequestLabel}
            disabled={isCanceling}
            onClick={handleCancel}
          >
            {cancelRequestLabel}
          </button>
        </div>
        {isCanceling && (
          <div className={styles.modalBusyOverlay} role="status" aria-label={cancelRequestLabel}>
            <XboxSpinner />
          </div>
        )}
      </div>
    </Modal>
  );
  const isReviewPending = !state.isApprovedAwaitingNda && !state.hasNdaSent;
  const statusTitle = state.isApprovedAwaitingNda
    ? approvedAwaitingNdaTitle.replace("{0}", state.ndaRecipientEmail ?? "")
    : state.hasNdaSent
    ? ndaSentTitle
        .replace("{0}", state.ndaNumber ?? "")
        .replace("{1}", state.ndaRecipientEmail ?? "")
    : reviewTitle
        .replace("{0}", currentLegalEntityName)
        .replace("{1}", requestedLegalEntityName ?? "");
  const hasActions = Boolean(cancelButton) || (state.hasNdaSent && state.canResendNda);

  return (
    <>
      <section
        className={`${styles.status} ${attached ? styles.statusAttached : ""} ${
          flush ? styles.statusFlush : ""
        }`}
        role="status"
        aria-busy={state.hasNdaSent ? isResending : undefined}
      >
        <div className={styles.statusContent}>
          <div className={styles.statusHeading}>
            <Icon aria-hidden="true" icon={Warning} iconSize={IconSize.Custom} />
            <h2>{statusTitle}</h2>
          </div>
          {!isReviewPending && requestedLegalEntityName && (
            <p>
              {requestedLegalEntityNameLabel
                .replace("{0}", currentLegalEntityName)
                .replace("{1}", requestedLegalEntityName)}
            </p>
          )}
          {isReviewPending && (
            <p>
              <span>
                {reviewDescription}{" "}
                <button
                  type="button"
                  className={styles.learnMoreButton}
                  onClick={() => setIsLearnMoreOpen(true)}
                >
                  {learnMoreLabel}
                </button>
              </span>
            </p>
          )}
          {requesterDetails}
          {(state.hasNdaSent || isReviewPending) && (
            <p>
              <Icon aria-hidden="true" icon={Clock} iconSize={IconSize.Custom} />
              {state.hasNdaSent
                ? ndaSentDescription.replace("{0}", state.ndaRecipientEmail ?? "")
                : reviewDuration}
            </p>
          )}
        </div>
        {hasActions && (
          <div className={styles.resendActions}>
            {state.hasNdaSent && state.canResendNda && (
              <button
                type="button"
                className={styles.resendButton}
                aria-label={resendLabel}
                disabled={isResending || !isResendCooldownLoaded || isNdaResendCooldownActive}
                onClick={handleResend}
              >
                {resendLabel}
              </button>
            )}
            {cancelButton}
            {state.hasNdaSent && hasResendError && <p role="alert">{resendError}</p>}
          </div>
        )}
        {state.hasNdaSent && isResending && (
          <div className={styles.statusBusyOverlay} role="status" aria-label={resendLabel}>
            <XboxSpinner />
          </div>
        )}
      </section>
      {isReviewPending && (
        <Modal
          isOpen={isLearnMoreOpen}
          onClose={() => setIsLearnMoreOpen(false)}
          ariaLabelledBy={titleId}
          initialFocus={ModalInitialFocus.FirstButton}
          width={448}
        >
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2 id={titleId}>{reviewModalTitle}</h2>
              <button
                type="button"
                aria-label={closeLabel}
                onClick={() => setIsLearnMoreOpen(false)}
              >
                <Icon aria-hidden="true" icon={Close} iconSize={IconSize.Custom} />
              </button>
            </div>
            <p>{reviewModalDescription}</p>
            <p>{reviewModalUnavailableTitle}</p>
            <ul>
              <li>{reviewModalDevKits}</li>
              <li>{reviewModalSigning}</li>
              <li>{reviewModalAccount}</li>
            </ul>
            <p>{reviewModalUpdate}</p>
            <div className={styles.duration}>
              <Icon aria-hidden="true" icon={Clock} iconSize={IconSize.Custom} />
              <span>{reviewModalDuration}</span>
            </div>
          </div>
        </Modal>
      )}
      {cancelModal}
    </>
  );
};
